import { Component, DestroyRef, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs';
import { AppInitService } from './pages/core/shared/services/app-init.service';
import { AuthRoleService } from './pages/core/shared/services/auth-role.service';
import { SpinnerService } from './pages/core/shared/services/spinner.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../environments/environment';
import { NavigationItem, SidepanelTypesV3, SidePanelV3 } from '@gea/digital-ui-lib';

@Component({
  selector: 'app-root',
  standalone: false,
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss'
})
export class AppComponent {
  title = 'NPE IO Test';
  navItems: NavigationItem[] = [];

  adminNavItems: NavigationItem[] = [];
  botItems: SidePanelV3[] = [];
  private isAdministrator = false;

  /** null = checking, true = access granted, false = access denied */
  hasAccess: boolean | null = environment.name.includes('cloud') ? null : true;

  /** false until AppInitService has stored the project tokens; the router outlet is not rendered before. */
  readonly tokensReady = signal(!environment.name.includes('cloud'));

  constructor(
    private appInitService: AppInitService,
    public spinnerService: SpinnerService,
    public translate: TranslateService,
    private readonly authRoleService: AuthRoleService,
    private readonly destroyRef: DestroyRef,
  ) {
    translate.addLangs(['en-US', 'es-ES']);
    translate.setDefaultLang('en-US');
    const browserLang = translate.getBrowserLang();
    translate.use(browserLang?.match(/en-US|es-ES/) ? browserLang : 'en-US');
  }

  ngOnInit() {

    this.navItems.push({
      label: 'UI-LIB.SIDE-BAR.IOTest',
      route: '/io-test',
      icon: 'technical-review-1',
    });
    this.navItems.push({
      label: 'UI-LIB.SIDE-BAR.IOIssues',
      route: '/io-issues',
      icon: 'wrench-tool',
    });
    this.navItems.push({
      label: 'UI-LIB.SIDE-BAR.IOEmailDistribution',
      route: '/io-email-distribution',
      icon: 'mail',
    });
    this.navItems.push({
      label: 'UI-LIB.SIDE-BAR.Dashboard',
      route: '/dashboard',
      icon: 'p-chart',
    });
    this.authRoleService.getRolesObservable().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(roles => {
      const isAdministrator = roles.some(role => role.roleName === 'Administrators');
      this.navItems = this.navItems.filter(item => item.route !== '/io-test-config');
      if (isAdministrator) {
        const emailDistributionIndex = this.navItems.findIndex(item => item.route === '/io-email-distribution');
        this.navItems = [...this.navItems.slice(0, emailDistributionIndex), {
          label: 'UI-LIB.SIDE-BAR.IOTestConfig',
          route: '/io-test-config',
          icon: 'setup-preferences',
        }, ...this.navItems.slice(emailDistributionIndex)];
      }
      this.isAdministrator = isAdministrator;
      this.syncApiDocsItem();
    });

    console.log('Environment Name:', environment.name);
    if (environment.name.includes('cloud')) {
      console.log('Initializing app in cloud environment');
      this.appInitService.init();
      // Also released when the token exchange fails, so the interceptor's 401 renewal can still recover.
      this.appInitService.tokensReady$.pipe(take(1)).subscribe(() => this.tokensReady.set(true));
      this.appInitService.hasAccess$.subscribe(value => {
        if (value !== null) {
          this.hasAccess = value;
          this.botItems = [{
            key: 'manuals',
            icon: 'folder',
            label: 'Manuals',
            type: SidepanelTypesV3.ROUTER,
            router: {
              url: '/admin/manuals',
              target: '/admin/manuals'
            }
          }];
          this.syncApiDocsItem();
        }
      });
    }
  }

  private syncApiDocsItem(): void {
    if (!environment.name.includes('cloud') || this.hasAccess === null) {
      return;
    }

    this.botItems = this.botItems.filter(item => item.key !== 'api-docs');
    if (!this.isAdministrator) {
      return;
    }

    this.botItems = [...this.botItems, {
      key: 'api-docs',
      icon: 'puzzle',
      label: 'API Docs',
      type: SidepanelTypesV3.ROUTER,
      router: {
        url: '/admin/api-docs',
        target: '/admin/api-docs'
      }
    }];
  }
}